import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Types
export interface InventoryItem {
  _id: string;
  siteId: string;
  name: string;
  code: string;
  category: string;
  description?: string;
  unit: string;
  currentStock: number;
  minimumStock: number;
  maximumStock?: number;
  reorderLevel: number;
  reorderQuantity: number;
  unitCost: number;
  averageCost: number;
  supplier?: string;
  supplierCode?: string;
  barcode?: string;
  location?: string;
  expiryDate?: string;
  batchNumber?: string;
  notes?: string;
  isLowStock: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryTransaction {
  _id: string;
  inventoryId: string;
  siteId: string;
  transactionType: 'purchase' | 'sale' | 'adjustment' | 'return' | 'damage' | 'loan' | 'loan_return';
  quantity: number;
  oldStock: number;
  newStock: number;
  unitCost: number;
  totalCost: number;
  reference?: string;
  referenceType?: 'order' | 'purchase_order' | 'adjustment' | 'loan';
  referenceId?: string;
  notes?: string;
  processedBy: string;
  processedAt: string;
  batchNumber?: string;
  expiryDate?: string;
  supplier?: string;
  inventory?: {
    name: string;
    code: string;
  };
  processedByUser?: {
    name: string;
  };
}

export interface CreateInventoryItem {
  siteId: string;
  name: string;
  code: string;
  category: string;
  description?: string;
  unit: string;
  currentStock?: number;
  minimumStock?: number;
  maximumStock?: number;
  reorderLevel?: number;
  reorderQuantity?: number;
  unitCost?: number;
  supplier?: string;
  supplierCode?: string;
  barcode?: string;
  location?: string;
  expiryDate?: string;
  batchNumber?: string;
  notes?: string;
}

export interface UpdateInventoryItem {
  name?: string;
  code?: string;
  category?: string;
  description?: string;
  unit?: string;
  currentStock?: number;
  minimumStock?: number;
  maximumStock?: number;
  reorderLevel?: number;
  reorderQuantity?: number;
  unitCost?: number;
  supplier?: string;
  supplierCode?: string;
  barcode?: string;
  location?: string;
  expiryDate?: string;
  batchNumber?: string;
  notes?: string;
}

export interface StockUpdate {
  quantity: number;
  transactionType: 'purchase' | 'sale' | 'adjustment' | 'return' | 'damage' | 'loan' | 'loan_return';
  reference?: string;
  notes?: string;
  unitCost?: number;
}

export interface GetItemsParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  lowStock?: boolean;
}

export interface GetTransactionsParams {
  page?: number;
  limit?: number;
  inventoryId?: string;
  transactionType?: string;
  startDate?: string;
  endDate?: string;
}

export interface InventorySummary {
  totalItems: number;
  lowStockCount: number;
  totalValue: number;
  categoryBreakdown: Array<{
    _id: string;
    count: number;
    totalValue: number;
  }>;
}

// API Functions
export const inventoryAPI = {
  // Inventory Items
  getItems: (siteId: string, params?: GetItemsParams) => {
    return api.get(`/inventory/sites/${siteId}/items`, { params });
  },

  getItem: (id: string) => {
    return api.get(`/inventory/items/${id}`);
  },

  createItem: (data: CreateInventoryItem) => {
    return api.post('/inventory/items', data);
  },

  updateItem: (id: string, data: UpdateInventoryItem) => {
    return api.put(`/inventory/items/${id}`, data);
  },

  deleteItem: (id: string) => {
    return api.delete(`/inventory/items/${id}`);
  },

  // Stock Management
  updateStock: (id: string, data: StockUpdate) => {
    return api.put(`/inventory/items/${id}/stock`, data);
  },

  // Transactions
  getTransactions: (siteId: string, params?: GetTransactionsParams) => {
    return api.get(`/inventory/sites/${siteId}/transactions`, { params });
  },

  // Reports
  getLowStockItems: (siteId: string) => {
    return api.get(`/inventory/sites/${siteId}/low-stock`);
  },

  getSummary: (siteId: string) => {
    return api.get(`/inventory/sites/${siteId}/summary`);
  }
};

export default inventoryAPI;
